import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

let cached: string | null | undefined;
let pending: Promise<string | null> | null = null;

async function fetchSitekey(): Promise<string | null> {
  if (cached !== undefined) return cached;
  if (pending) return pending;
  pending = supabase.functions.invoke("hcaptcha-sitekey").then(({ data }) => {
    cached = data?.sitekey ?? null;
    return cached;
  }).catch(() => {
    cached = null;
    return null;
  }).finally(() => { pending = null; });
  return pending;
}

export function useHCaptchaSitekey() {
  const [sitekey, setSitekey] = useState<string | null | undefined>(cached);
  useEffect(() => {
    if (cached === undefined) fetchSitekey().then(setSitekey);
  }, []);
  return sitekey;
}

export interface HCaptchaVerifyResult {
  success: boolean;
  errors: string[];
  reason?: string;
}

// Map hCaptcha error codes to readable reasons
const ERROR_REASONS: Record<string, string> = {
  "missing-input-secret": "Server is missing hCaptcha secret",
  "invalid-input-secret": "Invalid hCaptcha secret on server",
  "missing-input-response": "Captcha not completed",
  "invalid-input-response": "Captcha token is invalid",
  "expired-input-response": "Captcha expired — please retry",
  "already-seen-response": "Captcha already used — please retry",
  "bad-request": "Malformed captcha request",
  "invalid-or-already-seen-response": "Captcha invalid or replayed",
  "sitekey-secret-mismatch": "Sitekey/secret mismatch",
};

export async function verifyHCaptchaToken(token: string, formName: string): Promise<HCaptchaVerifyResult> {
  const { data, error } = await supabase.functions.invoke("verify-hcaptcha", {
    body: { token, formName },
  });
  if (error) {
    return { success: false, errors: ["network-error"], reason: error.message || "Network error reaching captcha verifier" };
  }
  const errs: string[] = data?.errors || [];
  const success = !!data?.success;
  const reason = success ? undefined : (errs.map((e) => ERROR_REASONS[e] || e).join("; ") || "Captcha verification failed");
  return { success, errors: errs, reason };
}